import { View, Text, ScreenContainer, Switch } from "@src/components";
import { sizes } from "@src/constants/sizes";
import { Redirect, useRouter } from "expo-router";
import { useUnistyles, StyleSheet } from "react-native-unistyles";
import { FocusedSystemBars } from "@src/components/FocusedSystemBars";
import { BackgroundGradient } from "@src/components/BackgroundGradient";
import { FEATURE_FLAGS, useBooleanFlag } from "@src/services/feature-flags/feature-flags";
import { usePushNotificationSwitch } from "@src/features/notifications";
import { Icon } from "@src/components/Icon";
import { Button } from "@src/components/Button";
import { Pressable } from "react-native";
import { appStorage, AppStorageKeys } from "@src/services/app-storage";
import { track } from "@visioni/a3s-analytics";
import { PushNotificationsOnboardingCompleted } from "@src/features/notifications/analytics";

export default function PushNotificationsOnboardingScreen() {
  const router = useRouter();
  const { theme } = useUnistyles();

  const isOnboardingEnabled = useBooleanFlag(FEATURE_FLAGS.PUSH_NOTIFICATIONS);

  const { isEnabled, isLoading, isBusy, isDeniedAtOsLevel, onToggle, onOpenSystemSettings } = usePushNotificationSwitch(
    { enabled: isOnboardingEnabled, feedback: "unexpectedErrorsOnly" },
  );

  const handleContinue = () => {
    track(PushNotificationsOnboardingCompleted, {
      push_enabled: isEnabled && !isDeniedAtOsLevel,
      os_permission_denied: isDeniedAtOsLevel,
    });
    appStorage.set(AppStorageKeys.PUSH_NOTIFICATIONS_ONBOARDING_SEEN, true);
    router.replace("/overview");
  };

  if (!isOnboardingEnabled) {
    return <Redirect href="/overview" />;
  }

  return (
    <>
      <FocusedSystemBars style="light" />
      <BackgroundGradient>
        <ScreenContainer edges={["top", "bottom"]} style={styles.screen} contentMaxWidth={sizes.maxWidthSm} scrollable>
          <View style={styles.headerSpacer} />

          <View alignItems="center" justifyContent="center" mt="11xl">
            <Icon
              name={isEnabled && !isDeniedAtOsLevel ? "bellOutline" : "bellOffOutline"}
              size={64}
              color={theme.colors.white.default}
            />
          </View>

          <View flex={1} px="4xl">
            <View mt="9xl" alignItems="center">
              <Text textStyle="h3/regular" textAlign="center" style={styles.text}>
                Push Notifications
              </Text>
            </View>

            <View mt="4xl" alignItems="center" gap="xl">
              <Text textStyle="sm/regular" textAlign="center" style={styles.text}>
                Turn on push notifications for this device. You can choose which sites and cameras notify you in
                Settings → Notification Preferences.
              </Text>
            </View>

            <View mt="11xl">
              <View style={styles.card}>
                <View style={styles.row}>
                  <View flex={1}>
                    <Text textStyle="md/medium" style={styles.text}>
                      Enable Notifications
                    </Text>
                  </View>
                  <Switch
                    active={isEnabled}
                    onToggle={onToggle}
                    disabled={isLoading || isBusy || isDeniedAtOsLevel}
                    size="medium"
                  />
                </View>
              </View>
            </View>

            {isDeniedAtOsLevel && (
              <Pressable style={styles.card} onPress={onOpenSystemSettings}>
                <View style={styles.row}>
                  <View flex={1}>
                    <Text textStyle="sm/medium" style={styles.text}>
                      Open System Settings
                    </Text>
                    <Text textStyle="xs/regular" style={styles.secondaryText} mt="xs">
                      Enable push notifications in system settings. Tap to open.
                    </Text>
                  </View>
                  <Icon name="chevronRight" size={theme.spacing["7xl"]} color={theme.colors.white.default} />
                </View>
              </Pressable>
            )}

            <View>
              <Button
                label={isDeniedAtOsLevel ? "Skip" : "Continue"}
                textStyle="md/regular"
                size="md"
                variant="primary"
                variantColor="blue"
                onPress={handleContinue}
                disabled={isBusy}
              />
            </View>
          </View>
        </ScreenContainer>
      </BackgroundGradient>
    </>
  );
}

const styles = StyleSheet.create((theme) => ({
  screen: {
    backgroundColor: "transparent",
  },
  headerSpacer: {
    height: 56,
  },
  text: {
    color: theme.colors.interactive.buttons.default_foreground,
  },
  card: {
    borderRadius: theme.spacing.xl,
    borderWidth: 1,
    borderColor: theme.colors.interactive.controls.secondary_foreground,
    overflow: "hidden",
    marginBottom: theme.spacing["3xl"],
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: theme.spacing["3xl"],
    paddingHorizontal: theme.spacing.xl,
  },
  secondaryText: {
    color: theme.colors.interactive.controls.secondary_foreground,
  },
}));
